import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CalendarRange, ClipboardCheck, Trophy } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { StatusBadge } from '@/components/common/Badge';
import { ConfirmDialog } from '@/components/common/Modal';
import { ErrorState, LoadingState } from '@/components/common/States';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { examinationsApi } from '@/services/api/endpoints/examinations';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { ROUTES } from '@/app/router/routes';
import { PERMISSIONS } from '@/types/permissions';
import type { ExaminationStatus } from '@/types/examination';
import { formatDate, formatDateTime, orDash } from '@/utils/format';
import { ExaminationPapersPanel } from './ExaminationPapersPanel';
import { ExamSchedulePanel } from './ExamSchedulePanel';
import { transitionsFor } from './shared';

/**
 * One examination: what it is, where it is in its lifecycle, its papers and its
 * timetable.
 *
 * The lifecycle buttons offered come from the same transition map the backend
 * enforces, and each one says what it does rather than only naming the next
 * status — "publish schedule" is a decision a person makes, "SCHEDULED" is not.
 */
export function ExaminationDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [confirming, setConfirming] = useState<{
    to: ExaminationStatus;
    label: string;
    hint: string;
  } | null>(null);

  const query = useQuery({
    queryKey: queryKeys.examinations.detail(id),
    queryFn: () => examinationsApi.get(id),
    enabled: id !== '',
  });

  const changeStatus = useMutation({
    mutationFn: (status: ExaminationStatus) => examinationsApi.changeStatus(id, status),
    onSuccess: (examination) => {
      toast.success(`Examination is now ${examination.status.replaceAll('_', ' ').toLowerCase()}`);
      setConfirming(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.examinations.all });
    },
    onError: (error) => {
      setConfirming(null);
      handleApiError(error);
    },
  });

  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  if (query.isPending) {
    return <LoadingState label="Loading the examination" />;
  }

  const examination = query.data;
  const steps = can(PERMISSIONS.EXAMINATION_STATUS) ? transitionsFor(examination.status) : [];

  const facts: { label: string; value: string }[] = [
    { label: 'Class', value: `${examination.gradeName} · ${orDash(examination.campusName)}` },
    { label: 'Type', value: examination.examinationTypeName },
    { label: 'Term', value: orDash(examination.termName) },
    { label: 'Academic year', value: examination.academicYearName },
    {
      label: 'Dates',
      value: `${formatDate(examination.startDate)} – ${formatDate(examination.endDate)}`,
    },
    { label: 'Papers', value: `${examination.subjectCount}` },
    { label: 'Sittings scheduled', value: `${examination.scheduledCount}` },
    {
      label: 'Results published',
      value: examination.publishedAt === undefined ? 'Not yet' : formatDateTime(examination.publishedAt),
    },
  ];

  return (
    <div>
      <Link
        to={ROUTES.examinations}
        className="mb-3 inline-flex items-center gap-1.5 text-[13px] text-muted transition-colors hover:text-ink"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
        All examinations
      </Link>

      <PageHeader
        title={examination.name}
        description={examination.description ?? undefined}
        icon={<ClipboardCheck className="h-5 w-5" aria-hidden="true" />}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={examination.status} />
            {can(PERMISSIONS.RESULT_VIEW) && (
              <Link
                to={ROUTES.examinationResults(examination.id)}
                className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-surface px-2.5 text-xs font-medium text-ink shadow-xs ring-1 ring-inset ring-line transition-colors hover:bg-surface-2 hover:ring-line-strong"
              >
                <Trophy className="h-3.5 w-3.5" aria-hidden="true" />
                Results
              </Link>
            )}
            {steps.map((step, index) => (
              <Button
                key={step.to}
                size="sm"
                variant={index === 0 ? 'primary' : 'secondary'}
                onClick={() => setConfirming(step)}
              >
                {step.label}
              </Button>
            ))}
          </div>
        }
      />

      <Card className="mb-4 px-4 py-4">
        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
          {facts.map((fact) => (
            <div key={fact.label}>
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-subtle">
                {fact.label}
              </dt>
              <dd className="mt-0.5 text-sm text-ink">{fact.value}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-4 flex items-center gap-1.5 border-t border-line pt-3 text-xs text-muted">
          <CalendarRange className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="font-mono">{examination.code}</span>
          <span aria-hidden="true">·</span>
          {examination.configurable
            ? 'Papers and dates can still be changed.'
            : 'Papers and dates are fixed at this stage.'}
        </p>
      </Card>

      <div className="space-y-4">
        <ExaminationPapersPanel examination={examination} />
        <ExamSchedulePanel examination={examination} />
      </div>

      <ConfirmDialog
        open={confirming !== null}
        title={confirming?.label ?? ''}
        message={confirming?.hint ?? ''}
        confirmLabel={confirming?.label ?? 'Confirm'}
        loading={changeStatus.isPending}
        onConfirm={() => confirming !== null && changeStatus.mutate(confirming.to)}
        onCancel={() => setConfirming(null)}
      />
    </div>
  );
}
